/**
 * Saga-map progress shared by every level-based arcade game: which levels are open, best stars
 * per level, free skips (H4). Pure functions over a Progress record + thin localStorage wrappers
 * (key `arcade.<id>.map`). No Phaser, no Math.random — tested in Node.
 *
 * There are no lives, keys or timers here (H1/H5): a level opens only by clearing or skipping the
 * one before it, and every open level can be replayed for free, forever.
 */
export interface Progress {
  /** Highest level the player may open (1-based). Level n is playable iff n <= unlocked. */
  unlocked: number;
  /** level → best stars (1..3). An earlier level without stars was skipped or cleared before the map existed. */
  stars: Record<string, number>;
  /** Levels skipped for free after three losses (H4). Winning the level later clears the mark. */
  skipped: Record<string, true>;
}

export type NodeState = "done" | "skipped" | "current" | "locked";

export const CHAPTER_SIZE = 10;

export function emptyProgress(): Progress {
  return { unlocked: 1, stars: {}, skipped: {} };
}

const level1 = (n: unknown): number => (typeof n === "number" && Number.isFinite(n) ? Math.max(1, Math.floor(n)) : 1);

/** Repair a stored record and fold in the game's own pre-map save (`save.level` = next level to play). */
export function normalize(raw: Partial<Progress> | null | undefined, legacyLevel?: number): Progress {
  const stars: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw?.stars ?? {})) {
    const n = Number(k);
    if (Number.isInteger(n) && n > 0 && typeof v === "number" && v >= 1) stars[String(n)] = Math.min(3, Math.floor(v));
  }
  const skipped: Record<string, true> = {};
  for (const k of Object.keys(raw?.skipped ?? {})) if (Number.isInteger(Number(k)) && Number(k) > 0 && !stars[k]) skipped[k] = true;
  return { unlocked: Math.max(level1(raw?.unlocked), level1(legacyLevel)), stars, skipped };
}

export function starsOf(p: Progress, level: number): number {
  return p.stars[String(level)] ?? 0;
}

export function nodeState(p: Progress, level: number): NodeState {
  if (level > p.unlocked) return "locked";
  if (level === p.unlocked) return "current";
  return p.skipped[String(level)] ? "skipped" : "done";
}

export function totalStars(p: Progress): number {
  return Object.values(p.stars).reduce((a, b) => a + b, 0);
}

export interface WinOutcome {
  progress: Progress;
  prevStars: number;
  stars: number;
  /** True when this win opened a level that was locked before. */
  unlockedNext: boolean;
}

/**
 * Clearing level n keeps the best star count and opens n + 1. A level opened through a shared
 * `?level=` link beyond the player's road keeps its stars but does not skip the road ahead.
 * Pure: returns a new record.
 */
export function applyWin(p: Progress, level: number, stars: number): WinOutcome {
  const n = level1(level);
  const earned = Math.max(1, Math.min(3, Math.floor(stars)));
  const prevStars = starsOf(p, n);
  const best = Math.max(prevStars, earned);
  const skipped = { ...p.skipped };
  delete skipped[String(n)];
  const unlocked = n <= p.unlocked ? Math.max(p.unlocked, n + 1) : p.unlocked;
  return { progress: { unlocked, stars: { ...p.stars, [String(n)]: best }, skipped }, prevStars, stars: best, unlockedNext: unlocked > p.unlocked };
}

/** H4: a level that beat the player three times opens the next one for free. */
export function applySkip(p: Progress, level: number): { progress: Progress; unlockedNext: boolean } {
  const n = level1(level);
  if (n > p.unlocked) return { progress: p, unlockedNext: false };
  const unlocked = Math.max(p.unlocked, n + 1);
  const skipped = starsOf(p, n) ? { ...p.skipped } : { ...p.skipped, [String(n)]: true as const };
  return { progress: { ...p, unlocked, skipped }, unlockedNext: unlocked > p.unlocked };
}

/** Union of two records: the furthest road, the best stars, skip marks only where no stars. */
export function mergeProgress(a: Progress, b: Progress): Progress {
  const stars = { ...a.stars };
  for (const [k, v] of Object.entries(b.stars)) stars[k] = Math.max(stars[k] ?? 0, v);
  const skipped: Record<string, true> = {};
  for (const k of [...Object.keys(a.skipped), ...Object.keys(b.skipped)]) if (!stars[k]) skipped[k] = true;
  return { unlocked: Math.max(a.unlocked, b.unlocked), stars, skipped };
}

/** 0-based chapter of a level. */
export function chapterOf(level: number, size = CHAPTER_SIZE): number {
  return Math.floor((level1(level) - 1) / size);
}

/** First and last level of a 0-based chapter. */
export function chapterRange(chapter: number, size = CHAPTER_SIZE): [number, number] {
  return [chapter * size + 1, chapter * size + size];
}

/** Stars collected in a chapter, out of 3 per level. */
export function chapterStars(p: Progress, chapter: number, size = CHAPTER_SIZE): { got: number; max: number } {
  const [a, b] = chapterRange(chapter, size);
  let got = 0;
  for (let n = a; n <= b; n++) got += starsOf(p, n);
  return { got, max: size * 3 };
}

/** The map shows every open chapter plus the next one as a locked teaser (levels are endless). */
export function lastShownLevel(p: Progress, size = CHAPTER_SIZE, maxLevel = Infinity): number {
  return Math.min(maxLevel, chapterRange(chapterOf(p.unlocked, size) + 1, size)[1]);
}

// ------------------------------------------------------------------ storage (offline, per game)

function read(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

const legacyReaders = new Map<string, () => number | undefined>();

/** A game whose pre-map save is not `arcade.<id>.level` (e.g. Mozaik) tells the map how to read it. */
export function registerLegacyLevel(id: string, read: () => number | undefined): void {
  legacyReaders.set(id, read);
}

function legacyLevel(id: string): number | undefined {
  const custom = legacyReaders.get(id);
  if (custom) return custom();
  const save = read(`arcade.${id}`) as { level?: unknown } | null;
  return typeof save?.level === "number" ? save.level : undefined;
}

/** Session copy, so the road keeps moving when storage is disabled or a write fails. */
const session = new Map<string, Progress>();

/** The game's own pre-map save seeds the record once; after that the map record is the only truth. */
export function loadProgress(id: string): Progress {
  const raw = read(`arcade.${id}.map`) as Partial<Progress> | null;
  const kept = session.get(id);
  if (raw) return kept ? mergeProgress(normalize(raw), kept) : normalize(raw);
  if (kept) return kept;
  const migrated = normalize(null, legacyLevel(id));
  store(id, migrated);
  return migrated;
}

const storedListeners = new Set<(id: string) => void>();

/** Called after a game's map record is written (a win, a free skip, the one-time migration): cloud save sync listens. */
export function onProgressStored(listener: (id: string) => void): () => void {
  storedListeners.add(listener);
  return () => void storedListeners.delete(listener);
}

function store(id: string, p: Progress): void {
  session.set(id, p);
  try {
    localStorage.setItem(`arcade.${id}.map`, JSON.stringify(p));
  } catch {
    /* private mode: progress lives for this session only */
  }
  for (const listener of storedListeners) listener(id);
}

export function recordWin(id: string, level: number, stars: number): WinOutcome {
  const out = applyWin(loadProgress(id), level, stars);
  store(id, out.progress);
  return out;
}

export function recordSkip(id: string, level: number): { progress: Progress; unlockedNext: boolean } {
  const out = applySkip(loadProgress(id), level);
  store(id, out.progress);
  return out;
}
